import type { ShiftAssignment, ShiftAssignmentShiftRef } from '@/types/shift.types'
import type { ClockInWindow } from '@/features/attendance/types/attendance.types'
import { getAssignmentLocalDate } from './shiftDate'

export interface ClockInWindowMs {
  startMs: number
  endMs: number
  /** Earliest clock-in: shift start minus the early allowance. */
  opensMs: number
  /** Shift end plus the late cutoff. */
  closesMs: number
  allowPastEnd: boolean
}

function parseHHmmToMs(date: Date, hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number)
  const dt = new Date(date)
  dt.setHours(h, m, 0, 0)
  return dt.getTime()
}

/**
 * When clock-in opens and closes for a shift. The API's window wins: it reads
 * the shift times in the company's timezone, exactly as clock-in checks them,
 * so a device set to another zone (or one hour off after a DST change) still
 * opens on time. Reading the times in the browser's zone only covers an older
 * API that sends no window.
 */
export function getClockInWindowMs(
  assignment: ShiftAssignment | null,
  serverWindow?: ClockInWindow | null,
): ClockInWindowMs | null {
  if (serverWindow) {
    return {
      startMs: Date.parse(serverWindow.startsAt),
      endMs: Date.parse(serverWindow.endsAt),
      opensMs: Date.parse(serverWindow.opensAt),
      closesMs: Date.parse(serverWindow.closesAt),
      allowPastEnd: serverWindow.allowClockInPastEnd,
    }
  }
  if (!assignment) return null
  const shift =
    assignment.shiftId && typeof assignment.shiftId === 'object'
      ? (assignment.shiftId as ShiftAssignmentShiftRef)
      : null
  const startStr = assignment.startTime ?? shift?.startTime
  const endStr = assignment.endTime ?? shift?.endTime
  if (!startStr || !endStr) return null

  // Assignment-level values (custom slots) win over the template, as on the BE.
  const maxEarly =
    assignment.maxEarlyClockInMinutes ?? shift?.maxEarlyClockInMinutes ?? 10
  const lateCutoff =
    assignment.lateClockInCutoffMinutes ?? shift?.lateClockInCutoffMinutes ?? 0
  // Use the assignment date, not today, so tomorrow's shift never looks open.
  const assignmentDate = getAssignmentLocalDate(assignment) ?? new Date()
  const startMs = parseHHmmToMs(assignmentDate, startStr)
  let endMs = parseHHmmToMs(assignmentDate, endStr)
  if (endStr <= startStr) endMs += 24 * 60 * 60 * 1000
  return {
    startMs,
    endMs,
    opensMs: startMs - maxEarly * 60_000,
    closesMs: endMs + lateCutoff * 60_000,
    allowPastEnd:
      assignment.allowClockInPastEnd ?? shift?.allowClockInPastEnd ?? false,
  }
}
